import React, { type ComponentProps, useCallback, useEffect, useState } from 'react';
import {
  type LayoutChangeEvent,
  type StyleProp,
  StyleSheet,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  ReduceMotion,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import {
  BodyText,
  DecorativeText,
  DisplayText,
  DisplayWideText,
  LabelText,
  TitleText,
} from '../Text';

// ─── Components ─────────────────────────────────────────────────────────────

const AnimatedBodyText = (props: AnimatedNumberTextProps<typeof BodyText>) => {
  const { text, accessibilityLabel, duration, style, containerStyle, ...textProps } = props;

  return (
    <AnimatedNumberTextBase
      as={<BodyText {...textProps}>{text}</BodyText>}
      text={text}
      accessibilityLabel={accessibilityLabel}
      duration={duration}
      style={style}
      containerStyle={containerStyle}
    />
  );
};

const AnimatedDecorativeText = (props: AnimatedNumberTextProps<typeof DecorativeText>) => {
  const { text, accessibilityLabel, duration, style, containerStyle, ...textProps } = props;

  return (
    <AnimatedNumberTextBase
      as={<DecorativeText {...textProps}>{text}</DecorativeText>}
      text={text}
      accessibilityLabel={accessibilityLabel}
      duration={duration}
      style={style}
      containerStyle={containerStyle}
    />
  );
};

const AnimatedDisplayText = (props: AnimatedNumberTextProps<typeof DisplayText>) => {
  const { text, accessibilityLabel, duration, style, containerStyle, ...textProps } = props;

  return (
    <AnimatedNumberTextBase
      as={<DisplayText {...textProps}>{text}</DisplayText>}
      text={text}
      accessibilityLabel={accessibilityLabel}
      duration={duration}
      style={style}
      containerStyle={containerStyle}
    />
  );
};

const AnimatedDisplayWideText = (props: AnimatedNumberTextProps<typeof DisplayWideText>) => {
  const { text, accessibilityLabel, duration, style, containerStyle, ...textProps } = props;

  return (
    <AnimatedNumberTextBase
      as={<DisplayWideText {...textProps}>{text}</DisplayWideText>}
      text={text}
      accessibilityLabel={accessibilityLabel}
      duration={duration}
      style={style}
      containerStyle={containerStyle}
    />
  );
};

const AnimatedLabelText = (props: AnimatedNumberTextProps<typeof LabelText>) => {
  const { text, accessibilityLabel, duration, style, containerStyle, ...textProps } = props;

  return (
    <AnimatedNumberTextBase
      as={<LabelText {...textProps}>{text}</LabelText>}
      text={text}
      accessibilityLabel={accessibilityLabel}
      duration={duration}
      style={style}
      containerStyle={containerStyle}
    />
  );
};

const AnimatedTitleText = (props: AnimatedNumberTextProps<typeof TitleText>) => {
  const { text, accessibilityLabel, duration, style, containerStyle, ...textProps } = props;

  return (
    <AnimatedNumberTextBase
      as={<TitleText {...textProps}>{text}</TitleText>}
      text={text}
      accessibilityLabel={accessibilityLabel}
      duration={duration}
      style={style}
      containerStyle={containerStyle}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const AnimatedNumberText = {
  BodyText: AnimatedBodyText,
  DecorativeText: AnimatedDecorativeText,
  DisplayText: AnimatedDisplayText,
  DisplayWideText: AnimatedDisplayWideText,
  LabelText: AnimatedLabelText,
  TitleText: AnimatedTitleText,
} as const;

// ─── Base Component ─────────────────────────────────────────────────────────

/**
 * Renders text with fixed-width digit rails that animate from zero to their target values.
 *
 * NOTE: Digit size intentionally uses React state because its layout measurement determines when the
 *       digit columns can render; updating a shared value would not trigger that render.
 */
const AnimatedNumberTextBase = (props: AnimatedNumberTextBaseProps) => {
  const {
    as,
    text,
    accessibilityLabel = text,
    duration = DEFAULT_ANIMATION_DURATION,
    style,
    containerStyle,
  } = props;

  // ─── State ───────────────────────────────────────────────────────────

  const [digitSize, setDigitSize] = useState<DigitSize | undefined>(undefined);

  // ─── Derived ─────────────────────────────────────────────────────────

  const characters = text.split('');

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDigitRailLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;

    const digitHeight = height / DIGIT_RAIL_LINE_COUNT;

    if (width === 0 || digitHeight === 0) return;

    const rowHeight = digitHeight * DIGIT_ROW_HEIGHT_MULTIPLIER;

    setDigitSize({ width, height: digitHeight, rowHeight: rowHeight });
  }, []);

  // ─────────────────────────────────────────────────────────────────────

  const measurementContainerStyles = [style, styles.measurementText];

  return (
    <View
      accessible
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="text"
      style={[styles.container, containerStyle]}
    >
      {React.cloneElement(
        as,
        { accessible: false, onLayout: handleDigitRailLayout, style: measurementContainerStyles },
        DIGIT_RAIL,
      )}

      {React.cloneElement(as, { accessible: false, style: [style, styles.sizingText] }, text)}

      <View style={styles.characters}>
        {digitSize
          ? characters.map((character, index) => (
              <AnimatedCharacter
                key={`${index}-${character}`}
                as={as}
                character={character}
                digitSize={digitSize}
                digitPositionFromRight={characters.slice(index + 1).filter(checkIfDigit).length}
                duration={duration}
                style={style}
              />
            ))
          : null}
      </View>
    </View>
  );
};

const AnimatedCharacter = (props: AnimatedCharacterProps) => {
  const { as, character, digitSize, digitPositionFromRight, duration, style } = props;

  if (checkIfDigit(character)) {
    return (
      <AnimatedDigit
        as={as}
        digit={Number(character)}
        digitSize={digitSize}
        digitPositionFromRight={digitPositionFromRight}
        duration={duration}
        style={style}
      />
    );
  }

  return React.cloneElement(as, { accessible: false, style }, character);
};

const AnimatedDigit = (props: AnimatedDigitProps) => {
  const { as, digit, digitSize, digitPositionFromRight, duration, style } = props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const translateY = useSharedValue(-digitSize.rowHeight);

  // ─── State ───────────────────────────────────────────────────────────

  const [animationTiming] = useState(() =>
    createAnimationTiming({ digitPositionFromRight, duration }),
  );

  // ─── Derived ─────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(
    () => ({ width: digitSize.width, height: digitSize.height }),
    [digitSize.height, digitSize.width],
  );
  const railAnimatedStyles = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    const targetDigitIndex = DIGIT_TARGET_CYCLE_OFFSET + digit + 1;

    translateY.value = withDelay(
      animationTiming.delay,
      withTiming(-targetDigitIndex * digitSize.rowHeight, {
        duration: animationTiming.duration,
        easing: DEFAULT_EASING,
        reduceMotion: ReduceMotion.System,
      }),
    );
  }, [animationTiming, digit, digitSize.rowHeight, translateY]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={[styles.digitContainer, containerDynamicStyles]}>
      <Animated.View style={railAnimatedStyles}>
        {DIGIT_RAIL_CHARACTERS.map((character, index) => (
          <AnimatedDigitRow
            key={`${index}-${character}`}
            as={as}
            character={character}
            digitSize={digitSize}
            style={style}
          />
        ))}
      </Animated.View>
    </View>
  );
};

const AnimatedDigitRow = (props: AnimatedDigitRowProps) => {
  const { as, character, digitSize, style } = props;

  // ─── Derived ─────────────────────────────────────────────────────────

  const digitRowDynamicStyles = useStyle(
    () => ({ height: digitSize.rowHeight }),
    [digitSize.rowHeight],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const rowStyles = [style, styles.digitText, digitRowDynamicStyles];

  // ─────────────────────────────────────────────────────────────────────

  return React.cloneElement(as, { accessible: false, style: rowStyles }, character);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfDigit(character: string) {
  return DIGIT_PATTERN.test(character);
}

function createAnimationTiming(params: CreateAnimationTimingParams): AnimationTiming {
  const { digitPositionFromRight, duration } = params;

  const jitter = Math.round(Math.random() * MAX_ANIMATION_DELAY_JITTER);

  return {
    delay: digitPositionFromRight * DIGIT_STAGGER_DELAY + jitter,
    duration,
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DIGITS = Array.from({ length: 10 }, (_, digit) => String(digit));
const DIGIT_CYCLE_COUNT = 2;
const DIGIT_RAIL_CHARACTERS = [
  '',
  ...Array.from({ length: DIGIT_CYCLE_COUNT }, () => DIGITS).flat(),
];

const DIGIT_RAIL = DIGIT_RAIL_CHARACTERS.join('\n');
const DIGIT_RAIL_LINE_COUNT = DIGIT_CYCLE_COUNT * DIGITS.length + 1;

const DIGIT_TARGET_CYCLE_OFFSET = (DIGIT_CYCLE_COUNT - 1) * DIGITS.length;
const DIGIT_PATTERN = /^\d$/;

const DEFAULT_ANIMATION_DURATION = 2_760;
const DEFAULT_EASING = Easing.out(Easing.cubic);

const DIGIT_STAGGER_DELAY = 120;
const MAX_ANIMATION_DELAY_JITTER = 15;
const DIGIT_ROW_HEIGHT_MULTIPLIER = 1.2;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
  },
  characters: {
    position: 'absolute',
    top: 0,
    alignSelf: 'center',
    flexDirection: 'row',
  },
  digitContainer: {
    overflow: 'hidden',
  },
  digitText: {
    textAlign: 'center',
  },
  measurementText: {
    position: 'absolute',
    opacity: 0,
  },
  sizingText: {
    opacity: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type AnimatedNumberTextProps<ComponentType extends TextComponent> =
  TextComponentProps<ComponentType> & {
    text: string;
    accessibilityLabel?: string;
    duration?: number;
    style?: StyleProp<TextStyle>;
    containerStyle?: StyleProp<ViewStyle>;
  };

type AnimatedNumberTextBaseProps = {
  as: React.ReactElement<AnimatedTextElementProps>;
  text: string;
  accessibilityLabel?: string;
  duration?: number;
  style?: StyleProp<TextStyle>;
  containerStyle?: StyleProp<ViewStyle>;
};

type AnimatedCharacterProps = {
  as: React.ReactElement<AnimatedTextElementProps>;
  character: string;
  digitSize: DigitSize;
  digitPositionFromRight: number;
  duration: number;
  style?: StyleProp<TextStyle>;
};

type AnimatedDigitProps = {
  as: React.ReactElement<AnimatedTextElementProps>;
  digit: number;
  digitSize: DigitSize;
  digitPositionFromRight: number;
  duration: number;
  style?: StyleProp<TextStyle>;
};

type AnimatedDigitRowProps = {
  as: React.ReactElement<AnimatedTextElementProps>;
  character: string;
  digitSize: DigitSize;
  style?: StyleProp<TextStyle>;
};

type DigitSize = {
  width: number;
  height: number;
  rowHeight: number;
};

type CreateAnimationTimingParams = {
  digitPositionFromRight: number;
  duration: number;
};

type AnimationTiming = {
  delay: number;
  duration: number;
};

type AnimatedTextElementProps = Pick<
  ComponentProps<typeof BodyText>,
  'accessible' | 'onLayout' | 'style'
>;

type TextComponent =
  | typeof BodyText
  | typeof DecorativeText
  | typeof DisplayText
  | typeof DisplayWideText
  | typeof LabelText
  | typeof TitleText;

type TextComponentProps<ComponentType extends TextComponent> = Omit<
  ComponentProps<ComponentType>,
  | 'accessibilityLabel'
  | 'accessibilityRole'
  | 'accessible'
  | 'children'
  | 'onLayout'
  | 'ref'
  | 'style'
>;
